import { useMemo } from "react";
import { MessageThreadFragment, MessageThreadType } from "gql/graphql";
import moment from "moment";
import {
  OPEN_TASK_STATUS_TYPES,
  CLOSED_TASK_STATUS_TYPES,
  getTaskStatusDescription,
} from "modules/utils/Functions";
import { useAuth } from "modules/providers/auth-provider";

interface IProps {
  messageThread: MessageThreadFragment;
}

const useTask = ({ messageThread }: IProps) => {
  const { me } = useAuth();

  const task = messageThread.task;

  const isTaskOverDue = useMemo(() => {
    if (!task || !task.dueTime) return false;
    if (!OPEN_TASK_STATUS_TYPES.includes(task.statusType)) return false;
    return moment(task.dueTime).isBefore(moment());
  }, [task]);

  const isTaskClosed = useMemo(() => {
    if (!task) return false;
    return CLOSED_TASK_STATUS_TYPES.includes(task.statusType);
  }, [task]);

  const taskStatusDescription = useMemo(
    () => getTaskStatusDescription(task?.statusType),
    [task],
  );

  const isCreator = useMemo(() => {
    if (!task || !me) return false;
    return messageThread.createdUserId === me.id;
  }, [task, me, messageThread.createdUserId]);

  const isAssignee = useMemo(() => {
    if (!task || !me) return false;
    return task.assignedUserId === me.id;
  }, [task, me]);

  const isCreatorOrAssignee = useMemo(() => {
    if (!task || !me) return false;
    return isCreator || isAssignee;
  }, [task, me, isCreator, isAssignee]);

  const isTask = useMemo(
    () => MessageThreadType.Task === messageThread.type && !!task,
    [messageThread.type, task],
  );

  return {
    task,
    isTask,
    isTaskOverDue,
    isTaskClosed,
    taskStatusDescription,
    isCreator,
    isAssignee,
    isCreatorOrAssignee,
  };
};

export default useTask;
